import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Form, Button } from 'react-bootstrap';
import './index.css';

const escapeHtml = (unsafe) => {
    return unsafe
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#039;");
};

const Highlighter = ({ jsonData, maxHeight = '400px' }) => {
    const [phoneNumberFilter, setPhoneNumberFilter] = useState('');
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const [totalMatches, setTotalMatches] = useState(0);
    const [phoneNumbers, setPhoneNumbers] = useState([]);
    const contentRef = useRef(null);
    const searchInputRef = useRef(null);

    useEffect(() => {
        if (jsonData) {
            try {
                const parsedData = typeof jsonData === 'string' ? JSON.parse(jsonData) : jsonData;
                const numbers = [];
                
                if (parsedData.msisdnlist && Array.isArray(parsedData.msisdnlist)) {
                    parsedData.msisdnlist.forEach(item => {
                        if (item.phoneno) {
                            numbers.push(item.phoneno);
                        }
                    });
                }
                
                setPhoneNumbers(numbers);
                setPhoneNumberFilter('');
                setCurrentMatchIndex(0);
                setTotalMatches(0);
                
                setTimeout(() => {
                    if (searchInputRef.current) {
                        searchInputRef.current.focus();
                    }
                }, 100);
            } catch (e) {
                console.error('Error parsing JSON:', e);
                setPhoneNumbers([]);
            }
        }
    }, [jsonData]);

    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Enter' && totalMatches > 0) {
                e.preventDefault();
                handleNextMatch();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [totalMatches, currentMatchIndex]);

    const matchingPhoneNumbers = useMemo(() => {
        if (!phoneNumberFilter || phoneNumbers.length === 0) {
            return [];
        }
        
        return phoneNumbers
            .map((phone, index) => ({ phone, index, matches: phone.includes(phoneNumberFilter) }))
            .filter(item => item.matches);
    }, [phoneNumberFilter, phoneNumbers]);

    useEffect(() => {
        setTotalMatches(matchingPhoneNumbers.length);
        if (matchingPhoneNumbers.length > 0 && currentMatchIndex >= matchingPhoneNumbers.length) {
            setCurrentMatchIndex(0);
        }
    }, [matchingPhoneNumbers, currentMatchIndex]);

    const handlePhoneNumberChange = (e) => {
        const value = e.target.value;
        setPhoneNumberFilter(value);
        setCurrentMatchIndex(0);
    };

    const handleNextMatch = () => {
        if (totalMatches > 0) {
            const nextIndex = (currentMatchIndex + 1) % totalMatches;
            setCurrentMatchIndex(nextIndex);
            scrollToMatch(nextIndex);
        }
    };

    const handlePrevMatch = () => {
        if (totalMatches > 0) {
            const prevIndex = (currentMatchIndex - 1 + totalMatches) % totalMatches;
            setCurrentMatchIndex(prevIndex);
            scrollToMatch(prevIndex);
        }
    };

    const scrollToMatch = (index) => {
        if (contentRef.current && matchingPhoneNumbers[index]) {
            const phoneToFind = matchingPhoneNumbers[index].phone;
            const element = contentRef.current.querySelector(`[data-phone="${phoneToFind}"]`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    };

    useEffect(() => {
        if (totalMatches > 0) {
            scrollToMatch(currentMatchIndex);
        }
    }, [currentMatchIndex, totalMatches]);

    const highlightedContent = useMemo(() => {
        if (!jsonData) {
            return null;
        }

        const dataString = typeof jsonData === 'string' ? jsonData : JSON.stringify(jsonData, null, 2);

        if (!phoneNumberFilter || matchingPhoneNumbers.length === 0) {
            return (
                <pre style={{
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    maxWidth: '100%',
                    overflowX: 'auto',
                }}>
                    {dataString}
                </pre>
            );
        }

        const phonesToHighlight = new Set(matchingPhoneNumbers.map(item => item.phone));
        const currentPhone = matchingPhoneNumbers[currentMatchIndex]?.phone;
        
        const lines = dataString.split('\n');
        
        return (
            <pre style={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxWidth: '100%',
                overflowX: 'auto',
            }}>
                {lines.map((line, lineIndex) => {
                    let highlightedLine = line;
                    let shouldHighlight = false;
                    
                    for (const phone of phonesToHighlight) {
                        if (line.includes(`"${phone}"`)) {
                            const isCurrent = phone === currentPhone;
                            const className = isCurrent ? 'highlight-match active-highlight' : 'highlight-match';
                            highlightedLine = line.replace(
                                new RegExp(`"${escapeHtml(phone)}"`, 'g'),
                                `"<mark data-phone="${phone}" class="${className}">${phone}</mark>"`
                            );
                            shouldHighlight = true;
                            break; 
                        }
                    }
                    
                    return (
                        <div 
                            key={lineIndex} 
                            dangerouslySetInnerHTML={{ 
                                __html: shouldHighlight ? highlightedLine : escapeHtml(line) 
                            }} 
                        />
                    );
                })}
            </pre>
        );
    }, [jsonData, phoneNumberFilter, matchingPhoneNumbers, currentMatchIndex]);

    return (
        <div style={{ overflowY: 'auto', background: '#f8f9fa', maxHeight }}>
            <div style={{ 
                position: 'sticky', 
                top: 0, 
                backgroundColor: '#f8f9fa', 
                padding: '10px 0', 
                zIndex: 10,
                borderBottom: '1px solid #dee2e6',
                marginBottom: '10px'
            }}>
                <Form.Group className="mb-2">
                    <Form.Label>Search Phone Number</Form.Label>
                    <Form.Control
                        ref={searchInputRef}
                        type="text"
                        value={phoneNumberFilter}
                        onChange={handlePhoneNumberChange}
                        placeholder="Enter phone number to highlight (Press Enter to cycle)"
                    />
                </Form.Group>
                {totalMatches > 0 && (
                    <div className="d-flex justify-content-between align-items-center">
                        <span>Match {currentMatchIndex + 1} of {totalMatches}</span>
                        <div>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handlePrevMatch}
                                disabled={totalMatches <= 1}
                            >
                                Previous
                            </Button>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={handleNextMatch}
                                className="ml-2"
                                disabled={totalMatches <= 1}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>
            
            <div ref={contentRef}>
                {highlightedContent}
            </div>
        </div>
    );
};

export default Highlighter;